
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface LabeledSelectProps {
  label: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  required?: boolean;
  name?: string;
  searchable?: boolean;
}

const LabeledSelect = React.forwardRef<HTMLButtonElement, LabeledSelectProps>(
  ({ 
    label, 
    options, 
    value, 
    onChange, 
    placeholder = "Select an option", 
    error,
    disabled,
    icon,
    labelClassName,
    wrapperClassName,
    required = false,
    name,
    searchable = false,
  }, ref) => {
    const id = `select-${Math.random().toString(36).substring(2, 9)}`;
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    
    // Filter options based on search term
    const filteredOptions = searchable 
      ? options.filter(option => {
          const label = option?.label || '';
          const value = option?.value || '';
          return label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 value.toLowerCase().includes(searchTerm.toLowerCase());
        })
      : options;
    
    return (
      <div className={cn("space-y-2", wrapperClassName)}>
        <Label 
          htmlFor={id} 
          className={cn(
            "flex items-center gap-2", 
            error && "text-destructive",
            labelClassName
          )}
        >
          {icon && icon}
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
        
        <Select 
          value={value} 
          onValueChange={(newValue) => {
            onChange(newValue);
            setSearchTerm('');
            setIsOpen(false);
          }}
          disabled={disabled}
          name={name}
          open={isOpen}
          onOpenChange={setIsOpen}
        >
          <SelectTrigger
            ref={ref}
            id={id}
            className="h-12 border-border bg-background hover:border-primary/50 focus:!border-primary focus:!ring-0 focus:!ring-offset-0 focus-visible:!border-primary focus-visible:!ring-0 focus-visible:!ring-offset-0"
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="z-[200] bg-background border-border shadow-lg">
            {searchable && (
              <div className="p-2 border-b border-border">
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsOpen(false);
                      setSearchTerm('');
                    }
                  }}
                />
              </div>
            )}
            <TooltipProvider delayDuration={150}>
              {filteredOptions.map((option) => {
                const item = (
                  <SelectItem
                    value={option.value}
                    className="hover:bg-muted focus:bg-muted"
                  >
                    {option.label}
                  </SelectItem>
                );

                if (!option.description) {
                  return React.cloneElement(item, { key: option.value });
                }

                return (
                  <Tooltip key={option.value}>
                    <TooltipTrigger asChild>{item}</TooltipTrigger>
                    <TooltipContent side="right" align="center" className="z-[300] max-w-xs text-xs leading-relaxed">
                      {option.description}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
            {searchable && filteredOptions.length === 0 && (
              <div className="p-2 text-sm text-muted-foreground text-center">
                No options found
              </div>
            )}
          </SelectContent>
        </Select>
        
        {error && (
          <p className="text-sm font-medium text-destructive">{error}</p>
        )}
      </div>
    );
  }
);

LabeledSelect.displayName = "LabeledSelect";

export { LabeledSelect };
